import { monthLabelFromIndex } from "../../../dashboard-framework/aggregateRows";

export type FinishedTreatmentItem = {
    id: number;
    date_if_finish: string;
    treatment_status_new: string;
    age_when_started: number | null;
};

export type TreatmentServiceCountRow = {
    treatment: number | string | null;
    service: number | string | null;
    count: string | number;
};

export type SessionsPerTreatment = {
    nfb: number;
    psy: number;
};

export const sessionsByTreatment = (rows: Array<TreatmentServiceCountRow>, nfbServiceId: number, psyServiceId: number) => {
    const countsByTreatment = new Map<string, SessionsPerTreatment>();
    rows.forEach((row) => {
        if (!row.treatment) return;
        const treatmentKey = String(row.treatment);
        const current = countsByTreatment.get(treatmentKey) || { nfb: 0, psy: 0 };
        if (Number(row.service) === nfbServiceId) current.nfb += Number(row.count);
        if (Number(row.service) === psyServiceId) current.psy += Number(row.count);
        countsByTreatment.set(treatmentKey, current);
    });
    return countsByTreatment;
};

const monthIndexFromIsoDate = (isoDate: string) => {
    const year = Number(isoDate.slice(0, 4));
    const month = Number(isoDate.slice(5, 7));
    const monthIndex = year * 12 + (month - 1);
    return monthIndex;
};

export type FinishedMonthAnalysis = {
    monthIndex: number;
    monthLabel: string;
    finished: number;
    success: number;
    declined: number;
    declinedPct: number;
    successPct: number;
    onlyPsy: number;
    onlyNfb: number;
    psyAndNfb: number;
    avgSessions: number | null;
};

export const buildFinishedTreatmentAnalysis = (
    treatments: Array<FinishedTreatmentItem>,
    sessionCounts: Map<string, SessionsPerTreatment>,
    successStatuses: Array<string>,
    declinedStatus: string
) => {
    if (treatments.length === 0) return [] as Array<FinishedMonthAnalysis>;

    const monthIndexes = treatments.map((treatment) => monthIndexFromIsoDate(treatment.date_if_finish));
    const firstMonthIndex = Math.min(...monthIndexes);
    const lastMonthIndex = Math.max(...monthIndexes);

    const months: Array<FinishedMonthAnalysis> = [];
    for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex++) {
        const monthTreatments = treatments.filter((treatment) => monthIndexFromIsoDate(treatment.date_if_finish) === monthIndex);

        const success = monthTreatments.filter((treatment) => successStatuses.includes(treatment.treatment_status_new)).length;
        const declined = monthTreatments.filter((treatment) => treatment.treatment_status_new === declinedStatus).length;
        const declinedPct = success + declined > 0 ? Math.round((declined / (success + declined)) * 1000) / 10 : 0;
        const successPct = success + declined > 0 ? Math.round((success / (success + declined)) * 1000) / 10 : 0;

        let onlyPsy = 0;
        let onlyNfb = 0;
        let psyAndNfb = 0;
        let sessionsSum = 0;
        let treatmentsWithSessions = 0;
        monthTreatments.forEach((treatment) => {
            const counts = sessionCounts.get(String(treatment.id));
            if (!counts || counts.nfb + counts.psy === 0) return;
            treatmentsWithSessions++;
            sessionsSum += counts.nfb + counts.psy;
            if (counts.nfb > 0 && counts.psy > 0) psyAndNfb++;
            else if (counts.psy > 0) onlyPsy++;
            else onlyNfb++;
        });

        const avgSessions = treatmentsWithSessions > 0 ? Math.round((sessionsSum / treatmentsWithSessions) * 100) / 100 : null;

        months.push({
            monthIndex,
            monthLabel: monthLabelFromIndex(monthIndex),
            finished: monthTreatments.length,
            success,
            declined,
            declinedPct,
            successPct,
            onlyPsy,
            onlyNfb,
            psyAndNfb,
            avgSessions,
        });
    }
    return months;
};

type ServiceCountRow = {
    service: number | string | null;
    count: string | number;
};

const serviceCount = (rows: Array<ServiceCountRow>, serviceId: number) => {
    const row = rows.find((candidateRow) => Number(candidateRow.service) === serviceId);
    const total = Number(row?.count || 0);
    return total;
};

export const futureSessionsPivotRows = (
    maioresRows: Array<ServiceCountRow>,
    menoresRows: Array<ServiceCountRow>,
    nfbServiceId: number,
    psyServiceId: number
) => {
    const buildRow = (clientType: string, rows: Array<ServiceCountRow>) => {
        const nfb = serviceCount(rows, nfbServiceId);
        const psy = serviceCount(rows, psyServiceId);
        const row = { clientType, nfb, psy, total: nfb + psy };
        return row;
    };

    const maiores = buildRow("Maiores 18", maioresRows);
    const menores = buildRow("Menores ou igual 18", menoresRows);
    const totals = {
        clientType: "Total",
        nfb: maiores.nfb + menores.nfb,
        psy: maiores.psy + menores.psy,
        total: maiores.total + menores.total,
    };
    const rows = [maiores, menores, totals];
    return rows;
};

export type PartnerJunctionRow = {
    professional_partners_id: number | string | null;
    treatments_id: number | string | null;
};

type PartnerInfo = {
    entity: string | null;
    specialization: string | null;
};

const distinctTreatmentsBy = <KeyType>(
    junctionRows: Array<PartnerJunctionRow>,
    keyForRow: (row: PartnerJunctionRow) => KeyType | null
) => {
    const idsByKey = new Map<KeyType, Set<string>>();
    junctionRows.forEach((row) => {
        if (!row.professional_partners_id || !row.treatments_id) return;
        const key = keyForRow(row);
        if (key === null) return;
        const treatmentIds = idsByKey.get(key) || new Set<string>();
        treatmentIds.add(String(row.treatments_id));
        idsByKey.set(key, treatmentIds);
    });
    return idsByKey;
};

const treatmentsWithoutPartner = (junctionRows: Array<PartnerJunctionRow>, totalTreatments: number) => {
    const allWithPartner = new Set(
        junctionRows.filter((row) => row.professional_partners_id && row.treatments_id).map((row) => String(row.treatments_id))
    );
    const withoutPartner = totalTreatments - allWithPartner.size;
    return withoutPartner;
};

export const partnerSpecializationRows = (
    junctionRows: Array<PartnerJunctionRow>,
    partnersById: Record<number, PartnerInfo>,
    totalTreatments: number
) => {
    const idsBySpecialization = distinctTreatmentsBy(junctionRows, (row) => {
        const partner = partnersById[Number(row.professional_partners_id)];
        const specialization = partner?.specialization || "(sem especialização)";
        return specialization;
    });

    const specializationRows = Array.from(idsBySpecialization.entries())
        .map(([specialization, treatmentIds]) => ({ specialization, treatments: treatmentIds.size }))
        .sort((firstRow, secondRow) => secondRow.treatments - firstRow.treatments || firstRow.specialization.localeCompare(secondRow.specialization));

    const rows = [{ specialization: "Sem parceiro", treatments: treatmentsWithoutPartner(junctionRows, totalTreatments) }, ...specializationRows];
    return rows;
};

export const partnerNameRows = (
    junctionRows: Array<PartnerJunctionRow>,
    partnersById: Record<number, PartnerInfo>,
    totalTreatments: number
) => {
    const idsByPartner = distinctTreatmentsBy(junctionRows, (row) => Number(row.professional_partners_id));

    const nameRows = Array.from(idsByPartner.entries())
        .map(([partnerId, treatmentIds]) => {
            const partner = partnersById[partnerId];
            const nameRow = {
                name: partner?.entity || "(sem nome)",
                specialization: partner?.specialization || "—",
                treatments: treatmentIds.size,
            };
            return nameRow;
        })
        .sort((firstRow, secondRow) => secondRow.treatments - firstRow.treatments || firstRow.name.localeCompare(secondRow.name));

    const rows = [{ name: "Sem parceiro", specialization: "—", treatments: treatmentsWithoutPartner(junctionRows, totalTreatments) }, ...nameRows];
    return rows;
};
